import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Pencil, X, Check } from "lucide-react";

export default function ProgramsPanel() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ slug: "", label_en: "", label_zh: "", desc_en: "", desc_zh: "", image_url: "", sort_order: 100 });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const { data } = await api.get("/programs"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function create(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/programs", { ...draft, sort_order: Number(draft.sort_order) || 100 });
      toast.success("Program added");
      setDraft({ slug: "", label_en: "", label_zh: "", desc_en: "", desc_zh: "", image_url: "", sort_order: 100 });
      load();
    } catch(e){ toast.error(getErrorDetail(e)); }
    finally { setSaving(false); }
  }
  async function saveEdit() {
    if (!editingId || !editDraft) return;
    setSaving(true);
    try {
      await api.patch(`/programs/${editingId}`, { ...editDraft, sort_order: Number(editDraft.sort_order) || 100 });
      toast.success("Program updated");
      setEditingId(null); setEditDraft(null);
      load();
    } catch(e){ toast.error(getErrorDetail(e)); }
    finally { setSaving(false); }
  }
  async function del(id) {
    if (!window.confirm("Delete this program?")) return;
    try { await api.delete(`/programs/${id}`); toast.success("Deleted"); load(); }
    catch(e){ toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-programs-panel" className="space-y-8">
      <form onSubmit={create} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4" data-testid="admin-programs-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Add a program</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Slug (lowercase, unique)</Label><Input required value={draft.slug} onChange={(e)=>setDraft({...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-")})} data-testid="programs-slug" className="mt-1" /></div>
          <div><Label>Sort order</Label><Input type="number" value={draft.sort_order} onChange={(e)=>setDraft({...draft, sort_order: e.target.value})} data-testid="programs-sort" className="mt-1" /></div>
          <div><Label>Label (English)</Label><Input required value={draft.label_en} onChange={(e)=>setDraft({...draft, label_en: e.target.value})} data-testid="programs-label-en" className="mt-1" /></div>
          <div><Label>Label (中文)</Label><Input value={draft.label_zh} onChange={(e)=>setDraft({...draft, label_zh: e.target.value})} data-testid="programs-label-zh" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Description (English)</Label><Textarea rows={2} required value={draft.desc_en} onChange={(e)=>setDraft({...draft, desc_en: e.target.value})} data-testid="programs-desc-en" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Description (中文)</Label><Textarea rows={2} value={draft.desc_zh} onChange={(e)=>setDraft({...draft, desc_zh: e.target.value})} data-testid="programs-desc-zh" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Image URL (optional)</Label><Input value={draft.image_url} onChange={(e)=>setDraft({...draft, image_url: e.target.value})} data-testid="programs-image" className="mt-1" placeholder="https://..." /></div>
        </div>
        <Button type="submit" disabled={saving} data-testid="programs-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{saving ? "…" : "Add program"}</Button>
      </form>

      <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
        {items.map((p) => (
          <li key={p.id} data-testid={`programs-row-${p.id}`} className="p-5">
            {editingId === p.id && editDraft ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Label (EN)</Label><Input value={editDraft.label_en} onChange={(e)=>setEditDraft({...editDraft, label_en: e.target.value})} className="mt-1" /></div>
                <div><Label className="text-xs">Label (中文)</Label><Input value={editDraft.label_zh || ""} onChange={(e)=>setEditDraft({...editDraft, label_zh: e.target.value})} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Desc (EN)</Label><Textarea rows={2} value={editDraft.desc_en} onChange={(e)=>setEditDraft({...editDraft, desc_en: e.target.value})} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Desc (中文)</Label><Textarea rows={2} value={editDraft.desc_zh || ""} onChange={(e)=>setEditDraft({...editDraft, desc_zh: e.target.value})} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Image URL</Label><Input value={editDraft.image_url || ""} onChange={(e)=>setEditDraft({...editDraft, image_url: e.target.value})} className="mt-1" /></div>
                <div><Label className="text-xs">Sort order</Label><Input type="number" value={editDraft.sort_order ?? 100} onChange={(e)=>setEditDraft({...editDraft, sort_order: e.target.value})} className="mt-1" /></div>
                <div className="md:col-span-2 flex gap-2">
                  <Button onClick={saveEdit} disabled={saving} data-testid={`programs-save-${p.id}`} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"><Check className="w-4 h-4 mr-1.5" />Save</Button>
                  <Button variant="outline" onClick={()=>{setEditingId(null); setEditDraft(null);}} data-testid={`programs-cancel-${p.id}`} className="rounded-full"><X className="w-4 h-4 mr-1.5" />Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{p.slug} · #{p.sort_order}</p>
                  <h4 className="font-display text-lg tracking-tight mt-1">{p.label_en}{p.label_zh ? ` · ${p.label_zh}` : ""}</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{p.desc_en}</p>
                </div>
                <div className="whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>{setEditingId(p.id); setEditDraft({...p});}} data-testid={`programs-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(p.id)} data-testid={`programs-delete-${p.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No programs yet.</li>}
      </ul>
    </div>
  );
}
